<template>
  <v-dialog v-model="visible" persistent max-width="638">
    <v-card class="v-dialog--custom" v-if="visible">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title my-2">{{ $t(`${namespace}.confirmBillingInformation`) }}</span>
        <v-btn class="v-dialog--custom__close mr-2" fab flat color="gray-6" :disabled="isLoading" @click="close">
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <form class="form--custom" novalidate>
        <div class="section section--border-b">
          <v-card-text class="pt-0 px-4">
            <label>{{ $t(`${namespace}.fieldCostCenterLabel`) }}</label>
            <AutoComplete
              kind="webCallCostCenter"
              :companyId="companyId"
              :employeeId="employeeId"
              requestOnClick
              :placeholder="$t(`${namespace}.fieldCostCenterPlaceholder`)"
              :isValid="isCostCenterValid"
              required
              @input="updateConstCenter"
            />

            <label>{{ $t(`${namespace}.fieldProjectLabel`) }}</label>
            <AutoComplete
              kind="webCallProject"
              :companyId="companyId"
              :costCenterId="form.costCenterID"
              requestOnClick
              :placeholder="$t(`${namespace}.searchActiveProject`)"
              :isValid="isProjectValid"
              :required="companyConfig.projectRequired"
              @input="updateProject"
            />

            <label>{{ $t(`${namespace}.fieldJustificationLabel`) }}</label>
            <v-text-field
              v-model="form.notes"
              :placeholder="$t(`${namespace}.fieldJustificationPlaceholder`)"
              :error-messages="errors.collect($t(`${namespace}.fieldJustificationName`))"
              v-validate="{
                required: companyConfig.showNote,
                min: companyConfig.minNoteLength,
                max: companyConfig.maxNoteLength
              }"
              :data-vv-name="$t(`${namespace}.fieldJustificationName`)"
              required
              flat
              outline
              solo
              color="accent"
            />
          </v-card-text>
        </div>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer/>
          <a
            v-if="!isLoading"
            href="#"
            class="text--normal accent--text mr-4"
            color="accent"
            flat
            round
            @click.prevent="close"
          >
            {{ $t('Common.cancel') }}
          </a>
          <v-btn
            type="button"
            :disabled="isLoading"
            :loading="isLoading"
            round
            depressed
            color="accent"
            @click="callCar"
          >
            {{ $t(`${namespace}.callAcar`) }}
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { isObject } from 'utils/functions'
import { Omega, OmegaEventIds, OmegaBusiness, OmegaStatus } from 'common/omega'
import AutoComplete from 'components/AutoComplete'

export default {
  name: 'WebCallBillingDataDialog',
  props: {
    data: {
      type: Object
    },
    visible: {
      type: Boolean,
      default: false
    },
    employeeId: [Number, String],
    companyId: {
      type: [Number, String]
    }
  },
  components: {
    AutoComplete
  },
  data () {
    return {
      namespace: 'Views.NewWebCallPage.webCallBillingDataDialog',

      // default
      isLoading: false,
      isDisabled: false,

      // autocomplete validations
      isCostCenterValid: true,
      isProjectValid: true,

      companyConfig: {
        maxNoteLength: 1024,
        minNoteLength: 0,
        projectRequired: false,
        showNote: false
      },

      // fields
      form: {
        costCenterID: null,
        projectID: null,
        notes: null
      }
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidObject (val) {
      return isObject(val)
    },

    updateConstCenter (val) {
      if (val && typeof val === 'object') {
        this.form.costCenterID = val.id
        this.isCostCenterValid = true
        return
      }

      this.isCostCenterValid = false
    },

    updateProject (val) {
      if (val && typeof val === 'object') {
        this.form.projectID = val.id
      }
    },

    resetAllFields () {
      // default
      this.isLoading = false
      this.isDisabled = false

      // autocomplete validations
      this.isCostCenterValid = true
      this.isProjectValid = true

      // fields
      this.form = {
        costCenterID: null,
        projectID: null,
        notes: null
      }
    },

    close () {
      this.$emit('close')
    },

    async getCompanyConfig () {
      try {
        const { data: res } = await this.$apiService.getCompanyConfig(this.companyId)
        this.companyConfig = { ...res.data }
      } catch (err) {
        console.log(err)
      }
    },

    async callCar () {
      // webcall账单信息模块确认操作
      Omega.trackBusiness(
        OmegaEventIds.tech_b2bv3_webcall,
        {
          business: OmegaBusiness.webcall,
          type: 'billingInfoSubmit',
          status: OmegaStatus.op
        }
      )
      if (!this.form.costCenterID) {
        this.isCostCenterValid = false
      }

      if (this.companyConfig.projectRequired && !this.form.projectID) {
        this.isProjectValid = false
      }

      try {
        const isValid = await this.$validator.validateAll()

        if (
          !isValid ||
          !this.form.costCenterID ||
          (this.companyConfig.projectRequired && !this.form.projectID)
        ) return

        this.isLoading = true

        await this.$apiService.callCar({
          ...this.data,
          ...this.form
        })

        this.isLoading = false
        this.successAlert(this.$t(`${this.namespace}.callAcarSuccess`))
        this.$emit('close')
        this.$emit('updateRidesList')
      } catch (err) {
        Omega.trackBusiness(
          OmegaEventIds.tech_b2bv3_webcall,
          {
            business: OmegaBusiness.webcall,
            type: 'billingInfoSubmit',
            status: OmegaStatus.error,
            msg: err?.message,
            params: {
              data: {
                categoryID: this.data?.categoryID,
                companyID: this.data?.companyID,
                employeeID: this.data?.employeeID
              },
              form: {
                costCenterID: this.form.costCenterID,
                projectID: this.form.projectID,
                notes: this.form?.notes
              }
            }
          }
        )
        this.errorAlert(this.$t(`${this.namespace}.callAcarError`))
        this.isLoading = false
        console.log(err)
      }
    }
  },
  watch: {
    'form.costCenterID' (val) {
      if (!val) {
        this.isCostCenterValid = false
        return
      }

      this.isCostCenterValid = true
    },

    'form.projectID' (val) {
      if (this.companyConfig.projectRequired && !val) {
        this.isProjectValid = false
        return
      }

      this.isProjectValid = true
    },

    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val
      this.resetAllFields()
      if (val) this.companyId && this.getCompanyConfig()
    }
  }
}
</script>
